/**
 * ManageTypesDialog — CRUD for custom document types
 *
 * Extracted from library/docs.tsx for reuse across Documents pages.
 * Shows built-in types (read-only) and allows adding/deleting custom types.
 *
 * LB6-T10 (decision 40, L6-13): the list is THE catalogue, read through the
 * one module (lib/use-document-types.ts) — the same query both type pickers
 * read, so a type added or deleted here is offered (or not) by the upload
 * dialog and the edit form at once. Offered on write:library.documents only
 * (LB6-T3, documents.tsx ManageTypesButton; OQ-L6-5).
 */

import { useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT,
  createDocumentType,
  deleteDocumentType,
  documentTypeKeyFor,
  invalidateDocumentTypes,
  useDocumentTypes,
} from '@/lib/use-document-types';
import { toast } from 'sonner';

interface ManageTypesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageTypesDialog({ open, onOpenChange }: ManageTypesDialogProps) {
  const queryClient = useQueryClient();
  const typesQ = useDocumentTypes({ enabled: open });
  const types = typesQ.data ?? [];
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    const name = documentTypeKeyFor(newLabel);
    if (!name) { toast.error('Invalid name'); return; }
    setAdding(true);
    try {
      await createDocumentType({ name, label: newLabel.trim() });
      toast.success(`Added: ${newLabel.trim()}`);
      setNewLabel('');
      await invalidateDocumentTypes(queryClient);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to add type'));
    }
    setAdding(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDocumentType(id);
      toast.success('Type removed');
      await invalidateDocumentTypes(queryClient);
    } catch (err: unknown) {
      // The server says why: still in use (409 IN_USE, with the count), or
      // already gone (404 — the catalogue is re-read so a type removed
      // elsewhere leaves the list too).
      toast.error(getApiErrorMessage(err, 'The type could not be removed.'));
      await invalidateDocumentTypes(queryClient);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Manage Document Types</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground">Built-in types cannot be removed. Add custom types for your organization.</p>

          <div className="max-h-[300px] overflow-auto space-y-1">
            {typesQ.isLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : typesQ.isError && !typesQ.data ? (
              <div role="alert" className="flex items-center justify-between px-3 py-2 text-xs text-destructive">
                <span>{DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT}</span>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => void typesQ.refetch()}>Retry</Button>
              </div>
            ) : (
              types.map(t => (
                <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded hover:bg-muted/30 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={t.isBuiltin ? 'secondary' : 'outline'} className="text-xs capitalize">{t.label}</Badge>
                    {t.isBuiltin && <span className="text-[9px] text-muted-foreground">built-in</span>}
                  </div>
                  {!t.isBuiltin && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(t.id)} title="Delete type" aria-label={`Delete type '${t.label}'`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center gap-2">
            <Input value={newLabel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLabel(e.target.value)} placeholder="e.g., Safety Data Sheet" className="flex-1" onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            <Button size="sm" onClick={handleAdd} disabled={!newLabel.trim() || adding}>
              <Plus className="h-4 w-4 mr-1" />{adding ? '...' : 'Add'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
